import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { enableKids, disableKids, formatApiError, kidsApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AgePicker } from "./AgePicker";

export const PinInput = ({ testid, value, onChange, placeholder }) => (
  <input
    data-testid={testid}
    type="password"
    inputMode="numeric"
    autoComplete="off"
    maxLength={6}
    value={value}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
    placeholder={placeholder}
    className="what-input text-center tracking-[0.5em] text-lg"
  />
);

export const KidsAgeSetter = () => {
  const { user, setUser } = useAuth();
  const [age, setAge] = useState(user?.kid_age || null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  if (user?.role === "student") return null;
  const save = async () => {
    setBusy(true);
    try {
      setUser(await kidsApi.setAge(age, pin));
      toast.success(`Answers are now pitched for a ${age}-year-old`);
      setPin("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };
  return (
    <div data-testid="kids-age-setter" className="space-y-3">
      <p className="text-sm font-semibold text-white">Child's age</p>
      <p className="text-xs text-slate-400">The AIs talk at your child's level and add harder words to tap and learn.</p>
      <AgePicker value={age} onChange={setAge} testid="kids-age" />
      <PinInput testid="kids-age-pin" value={pin} onChange={setPin} placeholder="Parent PIN to save" />
      <button data-testid="kids-age-save" type="button" onClick={save} disabled={!age || pin.length < 4 || busy} className="what-btn-ghost w-full disabled:opacity-40">
        {busy && <Loader2 size={14} className="animate-spin" />} Save age
      </button>
    </div>
  );
};

export const KidsDialog = ({ open, onOpenChange, onChanged }) => {
  const { user, setUser } = useAuth();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [usePassword, setUsePassword] = useState(false);
  const [age, setAge] = useState(user?.kid_age || null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const on = user?.kids_mode;
  const creating = !on && !user?.kids_pin_set;
  const askAge = !on && user?.role !== "student";

  const reset = (v) => { setPin(""); setConfirm(""); setPassword(""); setError(""); setUsePassword(false); setAge(user?.kid_age || null); onOpenChange(v); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (askAge && !age) return setError("Tick your child's age first");
    if (!usePassword && (pin.length < 4)) return setError("Enter a 4–6 digit PIN");
    if (creating && pin !== confirm) return setError("The PINs don't match");
    setBusy(true);
    try {
      const u = on ? await disableKids(usePassword ? { password } : { pin }) : await enableKids(pin, age);
      setUser(u);
      toast.success(u.kids_mode ? "Kids Mode is on" : "Kids Mode is off");
      onChanged();
      reset(false);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent data-testid="kids-dialog" className="max-w-md bg-[#0E131D] border-white/[0.08] text-slate-200 p-8 font-['Plus_Jakarta_Sans',sans-serif]">
        <div className="w-11 h-11 rounded-xl grid place-items-center bg-[#38BDF8]/10 text-[#38BDF8]"><ShieldCheck size={22} /></div>
        <DialogTitle className="font-display text-2xl font-bold text-white tracking-tight">
          {on ? "Turn off Kids Mode" : creating ? "Set up Kids Mode" : "Turn on Kids Mode"}
        </DialogTitle>
        <DialogDescription className="text-slate-400">
          {on
            ? "Enter the parent PIN to go back to the full app."
            : "Every question and answer is checked to be safe for children aged 5 to 9. Answers are pitched to your child's age. Your other chats, sharing and purchases are hidden."}
        </DialogDescription>
        {on && <KidsAgeSetter />}
        <form onSubmit={submit} className={`space-y-3 ${on && user?.role !== "student" ? "border-t border-white/[0.07] pt-5" : ""}`}>
          {askAge && (
            <div data-testid="kids-enable-age" className="space-y-2 pb-2">
              <p className="text-sm font-semibold text-white">Child's age</p>
              <AgePicker value={age} onChange={setAge} testid="kids-enable-age" />
            </div>
          )}
          {usePassword ? (
            <input data-testid="kids-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Account password" className="what-input" />
          ) : (
            <PinInput testid="kids-pin-input" value={pin} onChange={setPin} placeholder={creating ? "New parent PIN" : "Parent PIN"} />
          )}
          {creating && <PinInput testid="kids-pin-confirm-input" value={confirm} onChange={setConfirm} placeholder="Repeat PIN" />}
          {error && <p data-testid="kids-error" className="text-sm text-red-400">{error}</p>}
          <button data-testid="kids-submit-btn" disabled={busy} className="what-btn-primary w-full !py-3">
            {busy && <Loader2 size={15} className="animate-spin" />}
            {on ? "Turn off Kids Mode" : creating ? "Create PIN & turn on" : "Turn on Kids Mode"}
          </button>
          {on && (
            <button type="button" data-testid="kids-use-password-btn" onClick={() => { setUsePassword(!usePassword); setError(""); }} className="w-full text-xs text-slate-500 hover:text-slate-300">
              {usePassword ? "Use the parent PIN instead" : "Forgot the PIN? Use the account password"}
            </button>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
